import React from 'react';
import { TextField } from '@material-ui/core';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import useDebounce from '@forvalter/utils/useDebounce';
import { getLocalizedLabel, findCamundaPropertyValue } from '../../utils';
import type { FormField } from '../../interfaces';
import { useTextLengthFeedback } from '../../hooks/useTextLengthFeedback';

interface BasicTextFieldProps {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
    classes: {
        editableField: string;
        readOnlyField: string;
    };
}

export const BasicTextField: React.FC<BasicTextFieldProps> = ({
    field,
    isReadOnly,
    isRequired,
    classes,
}) => {
    const { id, label, properties } = field;
    const { values, setFieldValue, getFieldMeta, handleBlur } =
        useFormikContext<Record<string, unknown>>();

    const fieldValue = values[id] ?? '';
    const meta = getFieldMeta(id);

    // Local state for immediate UI updates
    const [localValue, setLocalValue] = React.useState(fieldValue);

    // Use debounced value for Formik updates
    const { debouncedValue } = useDebounce(localValue, 300);

    // Update local state when field value changes from outside
    React.useEffect(() => {
        setLocalValue(fieldValue);
    }, [fieldValue]);

    // Update Formik when debounced value changes
    React.useEffect(() => {
        if (debouncedValue !== fieldValue) {
            setFieldValue(id as string, debouncedValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    const lengthFeedback = useTextLengthFeedback(localValue, meta, {
        showCounter: false,
        isReadOnly,
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setLocalValue(newValue);
    };

    const handleFieldBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        // Immediately update Formik on blur to ensure validation
        setFieldValue(id as string, localValue);
        handleBlur(event);
    };

    return (
        <TextField
            variant="outlined"
            name={id}
            value={localValue}
            onChange={handleChange}
            onBlur={handleFieldBlur}
            className={classNames({
                [classes.editableField]: !isReadOnly,
                [classes.readOnlyField]: isReadOnly,
            })}
            id={id}
            label={getLocalizedLabel(`project.formFields.${id}`, label, id)}
            placeholder={
                findCamundaPropertyValue('placeholder', properties) || ''
            }
            type="text"
            fullWidth={true}
            multiline={true}
            required={isRequired}
            inputProps={{
                'aria-readonly': isReadOnly,
            }}
            disabled={isReadOnly}
            InputLabelProps={{
                shrink: true,
            }}
            helperText={lengthFeedback.helperText}
            error={lengthFeedback.error}
        />
    );
};
